import {
  type ChatAppError,
  type ChatAppErrorCode,
} from "@/lib/ai/chat-app-error";
import {
  type ChatQuotaStatusSnapshot,
  type ChatQuotaTier,
  isNearLimit,
  resolveRemainingPercentage,
} from "@/lib/ai/chat-quota-status";
import type { AiModelAccess } from "@/lib/ai/model-access";
import { isHighTierModelId } from "@/lib/ai/model-tier";
import { AVAILABLE_MODELS, type ModelId } from "@/lib/ai/models";

export type ChatQuotaStatusCardAction =
  | {
      kind: "upgrade";
    }
  | {
      kind: "switch-model";
      modelId: ModelId;
    };

export type ChatQuotaStatusCardState =
  | {
      kind: "hidden";
    }
  | {
      kind: "exceeded";
      tier: ChatQuotaTier;
      primaryAction?: ChatQuotaStatusCardAction;
      showSettingsAction: boolean;
      allAvailableTiersExceeded: boolean;
    }
  | {
      kind: "plan-required" | "restricted-model";
      primaryAction: Extract<ChatQuotaStatusCardAction, { kind: "upgrade" }>;
      showSettingsAction: boolean;
    }
  | {
      kind: "unavailable";
      message: string;
    };

export type ChatQuotaTierIndicatorState =
  | {
      kind: "hidden";
    }
  | {
      kind: "warning";
      remainingPercentage: number;
    }
  | {
      kind: "exceeded";
    };

const UNAVAILABLE_ERROR_CODES = new Set<ChatAppErrorCode>([
  "AI_MODEL_ACCESS_UNRESOLVED",
  "AI_QUOTA_STATUS_UNAVAILABLE",
]);

function resolveAvailableModelForTier(
  tier: ChatQuotaTier,
  currentModel: ModelId,
  aiModelAccess: Pick<AiModelAccess, "restrictedModelIds">,
) {
  return (
    AVAILABLE_MODELS.find((availableModel) => {
      if (availableModel.value === currentModel) {
        return false;
      }

      if (tier === "HIGH") {
        return (
          isHighTierModelId(availableModel.value) &&
          !aiModelAccess.restrictedModelIds.includes(availableModel.value)
        );
      }

      return !isHighTierModelId(availableModel.value);
    })?.value ?? null
  );
}

function resolveQuotaPrimaryAction(params: {
  currentTier: ChatQuotaTier;
  aiModelAccess: Pick<
    AiModelAccess,
    "canUseHighTierModels" | "restrictedModelIds"
  >;
  lowQuotaStatus: ChatQuotaStatusSnapshot;
  highQuotaStatus: ChatQuotaStatusSnapshot;
  currentModel: ModelId;
}): ChatQuotaStatusCardAction | undefined {
  const {
    currentTier,
    aiModelAccess,
    lowQuotaStatus,
    highQuotaStatus,
    currentModel,
  } = params;

  if (currentTier === "LOW") {
    if (!aiModelAccess.canUseHighTierModels) {
      return { kind: "upgrade" };
    }

    if (highQuotaStatus.isLoaded && !highQuotaStatus.isExceeded) {
      const highTierModel = resolveAvailableModelForTier(
        "HIGH",
        currentModel,
        aiModelAccess,
      );
      if (highTierModel) {
        return { kind: "switch-model", modelId: highTierModel };
      }
    }

    return undefined;
  }

  if (lowQuotaStatus.isLoaded && !lowQuotaStatus.isExceeded) {
    const lowTierModel = resolveAvailableModelForTier(
      "LOW",
      currentModel,
      aiModelAccess,
    );
    if (lowTierModel) {
      return { kind: "switch-model", modelId: lowTierModel };
    }
  }

  return undefined;
}

/**
 * chat 画面では settings の quota 詳細を再現せず、
 * 「今どう行動すべきか」に必要な view state だけへ圧縮する。
 */
export function resolveChatQuotaStatusCardState(params: {
  appError: ChatAppError | null;
  currentModel: ModelId;
  aiModelAccess: Pick<
    AiModelAccess,
    "currentOption" | "canUseHighTierModels" | "restrictedModelIds"
  >;
  lowQuotaStatus: ChatQuotaStatusSnapshot;
  highQuotaStatus: ChatQuotaStatusSnapshot;
}): ChatQuotaStatusCardState {
  const {
    appError,
    currentModel,
    aiModelAccess,
    lowQuotaStatus,
    highQuotaStatus,
  } = params;

  if (appError?.code === "AI_PLAN_REQUIRED") {
    return {
      kind: "plan-required",
      primaryAction: { kind: "upgrade" },
      showSettingsAction: true,
    };
  }

  if (appError?.code === "AI_MODEL_RESTRICTED") {
    return {
      kind: "restricted-model",
      primaryAction: { kind: "upgrade" },
      showSettingsAction: true,
    };
  }

  if (appError && UNAVAILABLE_ERROR_CODES.has(appError.code)) {
    return {
      kind: "unavailable",
      message: appError.message,
    };
  }

  if (!aiModelAccess.currentOption) {
    return {
      kind: "plan-required",
      primaryAction: { kind: "upgrade" },
      showSettingsAction: true,
    };
  }

  const currentTier: ChatQuotaTier = isHighTierModelId(currentModel)
    ? "HIGH"
    : "LOW";
  const currentStatus =
    currentTier === "HIGH" ? highQuotaStatus : lowQuotaStatus;
  const allAvailableTiersExceeded =
    currentTier === "HIGH"
      ? lowQuotaStatus.isLoaded &&
        lowQuotaStatus.isExceeded &&
        highQuotaStatus.isLoaded &&
        highQuotaStatus.isExceeded
      : (!aiModelAccess.canUseHighTierModels ||
          (highQuotaStatus.isLoaded && highQuotaStatus.isExceeded)) &&
        lowQuotaStatus.isLoaded &&
        lowQuotaStatus.isExceeded;

  const isExceeded =
    currentStatus.isExceeded || appError?.code === "AI_QUOTA_EXCEEDED";
  if (isExceeded) {
    return {
      kind: "exceeded",
      tier: currentTier,
      primaryAction: resolveQuotaPrimaryAction({
        currentTier,
        aiModelAccess,
        lowQuotaStatus,
        highQuotaStatus,
        currentModel,
      }),
      showSettingsAction: true,
      allAvailableTiersExceeded,
    };
  }

  return { kind: "hidden" };
}

export function resolveChatQuotaTierIndicatorState(params: {
  tier: ChatQuotaTier;
  aiModelAccess: Pick<AiModelAccess, "canUseHighTierModels" | "currentOption">;
  lowQuotaStatus: ChatQuotaStatusSnapshot;
  highQuotaStatus: ChatQuotaStatusSnapshot;
}): ChatQuotaTierIndicatorState {
  const { tier, aiModelAccess, lowQuotaStatus, highQuotaStatus } = params;

  if (!aiModelAccess.currentOption) {
    return { kind: "hidden" };
  }

  if (tier === "HIGH" && !aiModelAccess.canUseHighTierModels) {
    return { kind: "hidden" };
  }

  const status = tier === "HIGH" ? highQuotaStatus : lowQuotaStatus;
  if (!status.isLoaded || status.isUnlimited) {
    return { kind: "hidden" };
  }

  if (status.isExceeded) {
    return { kind: "exceeded" };
  }

  if (isNearLimit(status)) {
    return {
      kind: "warning",
      remainingPercentage: resolveRemainingPercentage(status) ?? 0,
    };
  }

  return { kind: "hidden" };
}
